import React, { useState, useEffect } from 'react';
import { API_BASE_URL, fetchWithCsrf } from '../../../config/api';
import HubPageLayout from '../components/HubPageLayout';
import PinFrame from '../components/PinFrame';
import CloseButton from '../components/CloseButton';
import useReturnTo from '../components/useReturnTo';

// "Report a new fraud or scam" — the Loop us in intake form. Submissions are
// stored in scam_reports and emailed to the intake mailbox. Both screens use
// the pin frame header shared with the ribbon-only pages (9-30 feedback).
//
// The mockup shows an "I'm not a robot" captcha; reCAPTCHA needs site/secret
// keys that aren't configured yet, so the slot is marked below rather than
// wired to a non-functional widget. The form is behind dashboard auth already.
const ReportPage = () => {
  const { goBack } = useReturnTo();
  const [form, setForm] = useState({ name: '', email: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill from the signed-in profile; the user can still override.
  useEffect(() => {
    fetch(`${API_BASE_URL}/bff/profile`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const p = d?.profile;
        if (!p) return;
        setForm((f) => ({
          ...f,
          name: f.name || p.display_name || '',
          email: f.email || p.email || '',
        }));
      })
      .catch(() => { /* form still works unfilled */ });
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetchWithCsrf(`${API_BASE_URL}/bff/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim() || null,
          email: form.email.trim() || null,
          description: form.description.trim(),
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.errors?.[0]?.msg || d.error || 'Failed to submit');
      }
      // The confirmation stays put. Leaving is the user's call — the close
      // button and the back link below both do it on demand.
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <HubPageLayout>
        <PinFrame
          title="Will investigate"
          corner={<CloseButton onClick={goBack} />}
          className="min-h-[520px]"
        >
          <p className="ml-1 text-lg text-black">Thanks for the tip.</p>
          <button
            type="button"
            onClick={goBack}
            className="mt-6 ml-1 text-lg text-blue-600 hover:underline"
          >
            &larr; Back to where you left off.
          </button>
        </PinFrame>
      </HubPageLayout>
    );
  }

  return (
    <HubPageLayout>
      <form onSubmit={submit}>
        <PinFrame title="Loop us in" corner={<CloseButton onClick={goBack} />}>
        <div className="flex flex-col lg:flex-row gap-10 ml-1">

          {/* Identity */}
          <div className="lg:w-2/5">
            <label className="block mt-4">
              <span className="sr-only">Name</span>
              <input
                type="text"
                placeholder="Name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                className="w-full text-lg text-black placeholder-gray-500 border-b border-black pb-2 focus:outline-none focus:border-blue-600"
              />
            </label>

            <label className="block mt-8">
              <span className="sr-only">Email</span>
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                className="w-full text-lg text-black placeholder-gray-500 border-b border-black pb-2 focus:outline-none focus:border-blue-600"
              />
            </label>
          </div>

          {/* The scam */}
          <div className="lg:flex-1">
            <h2 className="text-xl text-black text-center mb-3">
              Tell us about the scam
            </h2>
            <textarea
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              required
              rows={10}
              placeholder="What happened? Include how they contacted you, what they asked for, and any links, phone numbers or names involved."
              className="w-full border border-gray-400 p-4 text-black placeholder-gray-500 focus:outline-none focus:border-blue-600"
            />

            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mt-6">
              <p className="text-xs text-gray-500 max-w-sm">
                {/* reCAPTCHA slot — add the widget here once site keys are configured. */}
                Please see our <a href="/privacy-policy" className="underline">Privacy Policy</a> regarding
                how we will handle this information.
              </p>
              <button
                type="submit"
                disabled={submitting || !form.description.trim()}
                className="bg-black text-white text-lg px-10 py-3 rounded-lg border-2 border-gray-700 hover:bg-gray-900 disabled:opacity-50 whitespace-nowrap"
              >
                {submitting ? 'Sending…' : 'Report'}
              </button>
            </div>

            {error && <p className="text-red-600 mt-3">{error}</p>}
          </div>
        </div>
        </PinFrame>
      </form>
    </HubPageLayout>
  );
};

export default ReportPage;
